import type _jquery from "jquery";
import { provide } from "vue";
import { exhaustiveGuard } from "@mcwd/typescript-type-guards";
import {
  injectRequiredWithSelf,
  checkValuesAlreadyProvided,
  CreateInjectionKeysRecord,
  type InjectionKeysRecord,
  type InjectionValuesRecord
} from "@mcwd/util-vue";
import type { IFormSettings } from "../form-settings/form-settings-factory.js";
import type { AnyMarketoInputName } from "../types/definitions/inputs/marketo-input-names-and-values.js";
import { MarketoAllInputMeta } from "../types/definitions/inputs/marketo-input-meta.js";
import { MarketoFormSettingsObject } from "../form-settings/marketo-form/marketo-form-setting.js";
import { useGatingLevel } from "./use-gating-level.js";
import { useFormSettings } from "./use-form-settings.js";
import { useFormLogger } from "./use-form-logger.js";
import type { GatingState } from "../types/definitions/gating/gating-state.js";

type InputsMetaInjectionValues<EnabledField extends AnyMarketoInputName> = InjectionValuesRecord<{
  FormInputsMeta: Pick<MarketoAllInputMeta, EnabledField>;
}>;
type InputsMetaInjectionKeys<EnabledField extends AnyMarketoInputName> = InjectionKeysRecord<InputsMetaInjectionValues<EnabledField>>;

const InputsMetaInjectionKeys = CreateInjectionKeysRecord<InputsMetaInjectionKeys<AnyMarketoInputName>>({
  FormInputsMeta: "FormInputsMeta",
});

function getEnabledFields(formSettings: IFormSettings, currentGatingState: GatingState.MarketoForm) {
  const marketoFormSettings = MarketoFormSettingsObject[formSettings.marketoFormSettingsKey];
  const enabledFieldsObj = marketoFormSettings.enabledFields;
  switch (currentGatingState) {
    case "BlindSubmit": return ("BlindSubmit" in enabledFieldsObj) ? enabledFieldsObj.BlindSubmit : [];
    case "PartialGate": return ("PartialGate" in enabledFieldsObj) ? enabledFieldsObj.PartialGate : [];
    case "FullGate": return ("FullGate" in enabledFieldsObj) ? enabledFieldsObj.FullGate : [];
    default: exhaustiveGuard(currentGatingState);
  }
}

export function initInputsMetaData() {
  if (checkValuesAlreadyProvided(InputsMetaInjectionKeys)) {
    return;
  }

  const { FormSettings } = useFormSettings({ callerLabel: "initInputsMetaData" });
  const { CurrentMarketoFormGatingState } = useGatingLevel({ callerLabel: "initInputsMetaData" });
  
  const enabledFields = getEnabledFields(FormSettings as IFormSettings, CurrentMarketoFormGatingState.value);

  type EnabledField = typeof enabledFields[number];
  const FormInputsMeta = Object.fromEntries(
    enabledFields.map((field) => [field, MarketoAllInputMeta[field]] as const)
  ) as Pick<MarketoAllInputMeta, EnabledField>;

  provide(InputsMetaInjectionKeys.FormInputsMeta as InputsMetaInjectionKeys<EnabledField>["FormInputsMeta"], FormInputsMeta);
}

export function useInputsMetaData ({ callerLabel }: { callerLabel?: string | undefined } = {}) {
  const { logger } = useFormLogger();
  try {
    callerLabel ??= "useInputsMetaData";    
    type EnabledField = ReturnType<typeof getEnabledFields>[number]

    const FormInputsMeta = injectRequiredWithSelf(InputsMetaInjectionKeys.FormInputsMeta as InputsMetaInjectionKeys<EnabledField>["FormInputsMeta"]);
    return { FormInputsMeta };
  }
  catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? 'unknown'}`);
    throw err;
  }
}